/**
 * История «анализов по списку встреч» (TM-4c1fb82b).
 *
 * Тянет `GET /api/list_analyses` (опционально фильтруя по mode/employee/client)
 * и поллит каждые 30с пока есть pending/running записи. Каждая запись —
 * карточка со статус-бейджем, запросом, превью и кнопкой «Открыть» (viewer
 * с полным текстом через `GET /api/list_analyses/{id}`).
 *
 * Без пропсов-фильтров = все доступные анализы (раздел «Анализы»).
 */

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { getListAnalysis, listListAnalyses } from "@/api/lookups";
import type {
  ListAnalysisMode,
  ListAnalysisStatus,
  ListAnalysisSummary,
  UUID,
} from "@/api/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export interface ListAnalysisHistoryProps {
  mode?: ListAnalysisMode;
  employeeId?: UUID;
  clientId?: UUID;
}

const POLL_INTERVAL_MS = 30_000;

function hasActiveStatus(rows: ListAnalysisSummary[]): boolean {
  return rows.some((r) => r.status === "pending" || r.status === "running");
}

const STATUS_CLASSES: Record<ListAnalysisStatus, string> = {
  pending: "bg-muted text-muted-foreground",
  running: "bg-blue-100 text-blue-800",
  done: "bg-green-100 text-green-800",
  error: "bg-red-100 text-red-800",
};

/** Статус-бейдж анализа — общий для ленты истории и реестра «Анализы». */
export function ListAnalysisStatusBadge({
  status,
  testId,
}: {
  status: ListAnalysisStatus;
  testId?: string;
}) {
  const { t } = useTranslation();
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLASSES[status]}`}
      data-testid={testId}
    >
      {t(`listAnalysis.status.${status}`)}
    </span>
  );
}

export function ListAnalysisHistory({
  mode,
  employeeId,
  clientId,
}: ListAnalysisHistoryProps) {
  const { t, i18n } = useTranslation();
  const [openId, setOpenId] = useState<UUID | null>(null);

  const historyQ = useQuery({
    queryKey: ["list-analyses", { mode, employeeId, clientId }],
    queryFn: () =>
      listListAnalyses({
        mode,
        employee_id: employeeId,
        client_id: clientId,
        limit: 50,
      }),
    refetchInterval: (q) =>
      q.state.data !== undefined && hasActiveStatus(q.state.data)
        ? POLL_INTERVAL_MS
        : false,
  });

  const rows = historyQ.data ?? [];
  const fmtDate = (iso: string | null): string =>
    iso === null ? "" : new Date(iso).toLocaleString(i18n.language);

  return (
    <section className="space-y-2" data-testid="list-analysis-history">
      <h2 className="text-lg font-semibold">
        {t("listAnalysis.section.history", { count: rows.length })}
      </h2>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t("listAnalysis.section.empty")}
        </p>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <Card key={row.id} data-testid={`list-analysis-row-${row.id}`}>
              <CardContent className="space-y-2 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <ListAnalysisStatusBadge
                    status={row.status}
                    testId={`list-analysis-row-status-${row.id}`}
                  />
                  <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium text-muted-foreground">
                    {t(`listAnalysis.mode.${row.mode}`)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {t("listAnalysis.history.meetingsCount", {
                      count: row.meetings_count,
                    })}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {t("listAnalysis.history.createdAt", {
                      value: fmtDate(row.created_at),
                    })}
                  </span>
                  {row.finished_at !== null ? (
                    <span className="text-xs text-muted-foreground">
                      {t("listAnalysis.history.finishedAt", {
                        value: fmtDate(row.finished_at),
                      })}
                    </span>
                  ) : null}
                </div>

                <p className="text-xs text-muted-foreground">
                  {t("listAnalysis.history.models", {
                    names: row.llm_models.join(", "),
                  })}
                  {row.consensus_model !== null
                    ? ` · ${t("listAnalysis.history.consensus", {
                        name: row.consensus_model,
                      })}`
                    : ""}
                </p>

                <p className="line-clamp-2 text-sm text-foreground">
                  {row.title ?? row.query_text}
                </p>

                {row.status === "done" && row.result_preview !== null ? (
                  <p className="line-clamp-3 whitespace-pre-line text-xs text-muted-foreground">
                    {row.result_preview}
                  </p>
                ) : null}

                {row.status === "error" && row.error_message !== null ? (
                  <p className="text-xs text-destructive">
                    {t("listAnalysis.history.errorMessage", {
                      message: row.error_message,
                    })}
                  </p>
                ) : null}

                {row.status === "done" ? (
                  <div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setOpenId(row.id)}
                      data-testid={`list-analysis-row-open-${row.id}`}
                    >
                      {t("listAnalysis.history.openButton")}
                    </Button>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ListAnalysisViewerDialog
        analysisId={openId}
        onClose={() => setOpenId(null)}
      />
    </section>
  );
}

export interface ListAnalysisViewerDialogProps {
  analysisId: UUID | null;
  onClose: () => void;
}

export function ListAnalysisViewerDialog({
  analysisId,
  onClose,
}: ListAnalysisViewerDialogProps) {
  const { t, i18n } = useTranslation();
  const viewerQ = useQuery({
    queryKey: ["list-analysis", analysisId],
    queryFn: () => getListAnalysis(analysisId as UUID),
    enabled: analysisId !== null,
  });

  const data = viewerQ.data;
  const dateLabel =
    data !== undefined
      ? new Date(data.created_at).toLocaleString(i18n.language)
      : "";

  return (
    <Dialog open={analysisId !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-h-[80vh] max-w-3xl overflow-y-auto"
        data-testid="list-analysis-viewer-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {t("listAnalysis.viewer.title", { date: dateLabel })}
          </DialogTitle>
          <DialogDescription>
            {data !== undefined
              ? [
                  t("listAnalysis.history.tokens", {
                    input: data.input_tokens ?? "?",
                    output: data.output_tokens ?? "?",
                  }),
                  data.llm_model_used !== null
                    ? t("listAnalysis.history.model", {
                        name: data.llm_model_used,
                      })
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ")
              : ""}
          </DialogDescription>
        </DialogHeader>

        {data === undefined ? (
          <p className="text-sm text-muted-foreground">…</p>
        ) : (
          <div className="space-y-3 text-sm">
            <div>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t("listAnalysis.viewer.queryHeading")}
              </h3>
              <p className="whitespace-pre-line">{data.query_text}</p>
            </div>
            <div>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t("listAnalysis.viewer.resultHeading")}
              </h3>
              <pre className="whitespace-pre-wrap break-words rounded-md bg-muted/40 p-3 font-sans text-sm">
                {data.result_text ?? ""}
              </pre>
            </div>
          </div>
        )}

        <div className="flex justify-end pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            {t("listAnalysis.viewer.closeButton")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
